// How a receipt's card reads on every screen. Never more than the last four.
//
//   known card with a name   "Capital One Savor ·7502"
//   known card, no name yet  "·7502"  (joined without a second dot: "Marc ·2843")
//   last four not on the card list   "Unknown card ·1234"
//   no card read             null
//
// r: { person, cardLast4, cardKnown, cardLabel, cardInPlaid, paymentMethod }
// (the list rows carry these; cardFromDetail() builds it from a detail).

const last4 = (r) => (/^\d{4}$/.test(String(r?.cardLast4 || '')) ? r.cardLast4 : null);

export function cardText(r) {
  const l4 = last4(r);
  if (!l4) return null;
  if (r.cardKnown === false) return `Unknown card ·${l4}`;
  return r.cardLabel ? `${r.cardLabel} ·${l4}` : `·${l4}`;
}

/** "A · B", or "A ·7502" when B is a bare last four: never two dots in a row. */
export const joinCard = (a, card) => (card.startsWith('·') ? `${a} ${card}` : `${a} · ${card}`);

/** Who paid: the card's person, or the owner of the bank account a payment came from (the server's payer); never a sender's address. */
export function payerName(r) {
  if (r?.payer?.basis === 'account' && r.payer.label) return r.payer.label;
  if (!last4(r) || r.cardKnown === false) return null;
  return r.person && !String(r.person).includes('@') ? r.person : null;
}

/** "Angela · Capital One Savor ·7502", "Unknown card ·1234", "Marc", or "—". */
export function paidByText(r) {
  const who = payerName(r);
  const card = cardText(r);
  if (who && card) return joinCard(who, card);
  return card || who || '—';
}

/** Why there is no Chase charge to find, in plain words. */
export function noBankText(r) {
  const card = cardText(r);
  if (card && r.cardKnown !== false && r.cardInPlaid === false) return joinCard('Not in Chase', card);
  if (!card && /\bcash\b/i.test(String(r.paymentMethod || ''))) return 'Paid cash · no bank match';
  return card ? joinCard('No bank match expected', card) : 'No bank match expected';
}

/** The same fields from a receipt detail (receipt + card). */
export function cardFromDetail(d) {
  const r = d?.receipt || {};
  const c = d?.card || null;
  return {
    person: c?.person ?? null, cardLast4: r.cardLast4 ?? null, cardKnown: c ? c.known !== false : null,
    cardLabel: c?.label ?? null, cardInPlaid: c ? c.inPlaid : null, paymentMethod: r.paymentMethod ?? null,
  };
}
